import { gql, type TypedDocumentNode } from '@apollo/client'
import { QueryRef, useReadQuery } from '@apollo/client/react'
import { ErrorAlert, PageHeader, PaginationControl, RouteLoadingIndicator } from '@repo/shared-client/components/blocks'
import { NativeSelectFormField } from '@repo/shared-client/components/fields'
import {
  Button,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@repo/shared-client/components/ui'
import { Form, useForm } from '@repo/shared-client/form'
import { cn } from '@repo/shared-client/lib/utils'
import { createFileRoute, stripSearchParams, useNavigate, useRouterState, useSearch } from '@tanstack/react-router'
import EventCard from '@web/src/features/event/event-card/event-card'
import {
  type EventListFilterFormValues,
  eventListFilterFormValuesFromResolvedParams,
} from '@web/src/features/event/event-list-filter-types'
import EventListSideNav from '@web/src/features/event/event-list-side-nav/event-list-side-nav'
import EventListTopNav from '@web/src/features/event/event-list-top-nav/event-list-top-nav'
import EventsMenu from '@web/src/features/event/events-menu/events-menu'
import Footer from '@web/src/features/footer/footer'
import { useAuth } from '@web/src/hooks/use-auth'
import { queryAndPreload } from '@web/src/lib/apollo/query-and-preload'
import { useTranslation } from '@web/src/lib/intl/react'
import { CalendarIcon, LayoutGridIcon, LayoutListIcon, PlusIcon } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type {
  EventCategoryEnum,
  EventSortEnum,
  EventsListQuery,
  EventsListQueryVariables,
  EventTagsQuery,
  EventTagsQueryVariables,
} from 'types/graphql'
import { z } from 'zod'
import { eventListPageHeadFromLoaderData } from './list.seo'

const TAGS_QUERY: TypedDocumentNode<EventTagsQuery, EventTagsQueryVariables> = gql`
  query EventTagsQuery {
    eventTags {
      id
      name
    }
  }
`

const EVENTS_QUERY: TypedDocumentNode<EventsListQuery, EventsListQueryVariables> = gql`
  query EventsListQuery($page: Int, $filter: EventsFilter, $sortBy: EventSortEnum) {
    eventsPage(page: $page, filter: $filter, sortBy: $sortBy) {
      events {
        id
        slug
        name
        category
        tagLine
        startDate
        location
        locationExact
        admission
        capacity
        attendanceCounts {
          goingCount
        }
        distance
        visibility
        firstMedia {
          id
          ownerId
          imageUrlMedium
          reviewStatus
          nsfwRating
        }
        tags {
          id
          name
        }
        myAttendance {
          id
          status
        }
        groupId
        groupName
        groupSlug
        groupCoverImageUrlSmall
        groupCoverImageOffsetX
        groupCoverImageOffsetY
        groupCoverNsfwRating
        status
        ownerId
      }
      count
      perPage
    }
  }
`

type EventListLoaderDeps = {
  page: number
  name?: string
  distance?: number
  tags?: string
  categories?: string
  excludeTags?: string
  location?: string
  locationLat?: number
  locationLong?: number
  sortBy?: string
  pastEvents?: boolean
  myEvents?: boolean
  pendingEvents?: boolean
  showHosting?: boolean
}

function eventsListQueryVariablesFromDeps(deps: EventListLoaderDeps): EventsListQueryVariables {
  const excludeTagsList = deps.excludeTags ? deps.excludeTags.split(',') : []

  const categoryFilterList = deps.categories
    ? (deps.categories
        .split(',')
        .map((c) => c.trim())
        .filter(Boolean) as EventCategoryEnum[])
    : []

  return {
    page: deps.page,
    filter: {
      name: deps.name,
      distance: deps.distance,
      categories: categoryFilterList.length > 0 ? categoryFilterList : undefined,
      tags: deps.tags ? deps.tags.split(',') : [],
      excludeTags: excludeTagsList,
      location: deps.location,
      locationLat: deps.locationLat,
      locationLong: deps.locationLong,
      myEvents: deps.myEvents,
      myHosting: deps.showHosting,
      pendingEvents: deps.pendingEvents,
      pastEvents: deps.pastEvents,
    },
    sortBy: deps.sortBy as EventSortEnum,
  }
}

type EventListSearchInput = {
  page?: number
  name?: string
  distance?: number
  tags?: string
  categories?: string
  excludeTags?: string
  location?: string
  locationLat?: number
  locationLong?: number
  sortBy?: string
  pastEvents?: boolean
  myEvents?: boolean
  pendingEvents?: boolean
  showHosting?: boolean
}

function resolveEventListSearchParams(search: EventListSearchInput): EventListLoaderDeps {
  const myEvents = search.myEvents === true
  const pendingEvents = search.pendingEvents === true
  return {
    page: search.page ?? 1,
    name: search.name,
    distance: search.distance ?? (myEvents || pendingEvents ? 1000 : 250),
    tags: search.tags,
    categories: search.categories,
    excludeTags: search.excludeTags,
    location: search.location,
    locationLat: search.locationLat,
    locationLong: search.locationLong,
    sortBy: search.sortBy ?? 'START_DATE',
    pastEvents: search.pastEvents,
    myEvents: search.myEvents,
    pendingEvents: search.pendingEvents,
    showHosting: search.showHosting,
  }
}

export const Route = createFileRoute('/(public)/(main-layout)/event/list')({
  component: EventSearchPage,
  staticData: {
    hideFooter: true,
  },
  validateSearch: z.object({
    page: z.coerce.number().optional(),
    name: z.string().optional(),
    distance: z.coerce.number().optional(),
    tags: z.string().optional(),
    categories: z.string().optional(),
    excludeTags: z.string().optional(),
    location: z.string().optional(),
    locationLat: z.coerce.number().optional(),
    locationLong: z.coerce.number().optional(),
    sortBy: z.string().optional(),
    pastEvents: z.coerce.boolean().optional(),
    myEvents: z.coerce.boolean().optional(),
    pendingEvents: z.coerce.boolean().optional(),
    showHosting: z.coerce.boolean().optional(),
  }),
  search: {
    middlewares: [
      // Removes parameters if they match default values
      stripSearchParams({
        page: 1,
        name: '',
        distance: 250,
        excludeTags: '',
        tags: '',
        categories: '',
        location: '',
        locationLat: 0,
        locationLong: 0,
        sortBy: 'START_DATE',
        pastEvents: false,
        myEvents: false,
        pendingEvents: false,
        showHosting: false,
      }),
    ],
  },
  head: ({ loaderData, match }) => eventListPageHeadFromLoaderData(loaderData, match.search),
  loaderDeps: ({ search }) => resolveEventListSearchParams(search),
  loader: async ({ context, deps: d }) => {
    const deps = d as EventListLoaderDeps
    const [eventsResult, tagsResult] = await Promise.all([
      queryAndPreload(context, EVENTS_QUERY, { variables: eventsListQueryVariablesFromDeps(deps) }),
      queryAndPreload(context, TAGS_QUERY),
    ])
    const events = eventsResult.queryData?.eventsPage?.events ?? []
    return {
      eventsQueryRef: eventsResult.queryRef,
      tagsQueryRef: tagsResult.queryRef,
      seoList: {
        items: events.slice(0, 24).map((e) => ({
          name: e.name?.trim() || 'Event',
          path: `/event/${e.slug ?? e.id}`,
          imageUrl: e.firstMedia?.imageUrlMedium,
        })),
        totalCount: eventsResult.queryData?.eventsPage?.count ?? 0,
      },
    }
  },
})

function CreateEventButton() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { hasPermission, isAuthenticated } = useAuth()

  if (!(isAuthenticated && hasPermission('event:create'))) {
    return null
  }

  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Button
            aria-label={t('Create a new event')}
            onClick={() => navigate({ to: '/event/create' })}
            size="icon"
            type="button"
          >
            <PlusIcon className="size-4 stroke-3" />
          </Button>
        }
      />
      <TooltipContent>{t('Create a new event')}</TooltipContent>
    </Tooltip>
  )
}

type EventCardLayoutMode = 'compact' | 'regular'

function LayoutToggleButton({ layout, onToggle }: { layout: EventCardLayoutMode; onToggle: () => void }) {
  const { t } = useTranslation()
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Button
            aria-label={layout === 'compact' ? t('Switch to stacked event cards') : t('Switch to compact event cards')}
            className="shadow-sm"
            onClick={onToggle}
            size="icon"
            type="button"
            variant="secondary"
          >
            {layout !== 'compact' ? <LayoutGridIcon className="size-4" /> : <LayoutListIcon className="size-4" />}
          </Button>
        }
      />
      <TooltipContent>
        {layout === 'compact' ? t('Show image above details') : t('Show image beside details')}
      </TooltipContent>
    </Tooltip>
  )
}

function EventSearchPage() {
  const { t } = useTranslation()
  const loaderData = Route.useLoaderData()
  const search = useSearch({ strict: false })
  const resolvedSearch = useMemo(() => resolveEventListSearchParams(search), [search])
  const { data, error } = useReadQuery(loaderData.eventsQueryRef as QueryRef<EventsListQuery>)
  const { data: tagsData } = useReadQuery(loaderData.tagsQueryRef as QueryRef<EventTagsQuery>)
  const searchRef = useRef(search)
  searchRef.current = search
  const navigate = useNavigate()
  const { currentUser, hasPermission, isAuthenticated, loading: authLoading } = useAuth()
  const isRoutePending = useRouterState({ select: (s) => s.status === 'pending' })
  const [filterNavigatePending, setFilterNavigatePending] = useState(false)

  useEffect(() => {
    if (!isRoutePending) {
      setFilterNavigatePending(false)
    }
  }, [isRoutePending])

  useEffect(() => {
    if (authLoading) {
      return
    }
    if (!isAuthenticated && resolvedSearch.page > 2) {
      navigate({ to: '/auth/signup' } as never)
    }
  }, [authLoading, isAuthenticated, navigate, resolvedSearch.page])

  const isFilterLoading = filterNavigatePending && isRoutePending

  const showMyEvents = search.myEvents === true
  const showPendingEvents = search.pendingEvents === true

  const [cardLayout, setCardLayout] = useState<EventCardLayoutMode>(
    showMyEvents || showPendingEvents ? 'compact' : 'regular'
  )

  useEffect(() => {
    setCardLayout(showMyEvents || showPendingEvents ? 'compact' : 'regular')
  }, [showMyEvents, showPendingEvents])

  const resolvedEventsQuery = data
  const resolvedTagsQuery = tagsData
  const resolvedEventsPage = resolvedEventsQuery?.eventsPage ?? { events: [], count: 0, perPage: 1 }
  const resolvedEventTags = resolvedTagsQuery?.eventTags ?? []

  const tagsSplit = (csv?: string) => (csv ? csv.split(',') : [])

  const listFilterDefaults = useMemo(
    (): EventListFilterFormValues =>
      eventListFilterFormValuesFromResolvedParams({
        name: search.name,
        distance: resolvedSearch.distance,
        categories: tagsSplit(search.categories),
        tags: tagsSplit(search.tags),
        excludeTags: tagsSplit(search.excludeTags),
        location: search.location ?? currentUser?.profile?.location,
        locationLat: search.locationLat ?? currentUser?.profile?.locationLat,
        locationLong: search.locationLong ?? currentUser?.profile?.locationLong,
        sortBy: resolvedSearch.sortBy,
        pastEvents: search.pastEvents,
        showHosting: search.showHosting,
      }),
    [
      search.name,
      resolvedSearch.distance,
      search.categories,
      search.tags,
      search.excludeTags,
      search.location,
      search.locationLat,
      search.locationLong,
      resolvedSearch.sortBy,
      search.pastEvents,
      search.showHosting,
    ]
  )

  const listFilterResetValues = useMemo(
    (): EventListFilterFormValues =>
      eventListFilterFormValuesFromResolvedParams({
        name: undefined,
        distance: isAuthenticated ? (showMyEvents || showPendingEvents ? 1000 : 250) : 1000,
        categories: undefined,
        tags: undefined,
        excludeTags: undefined,
        location: currentUser?.profile?.location,
        locationLat: currentUser?.profile?.locationLat,
        locationLong: currentUser?.profile?.locationLong,
        sortBy: undefined,
        pastEvents: false,
        showHosting: false,
      }),
    [
      currentUser?.profile?.location,
      currentUser?.profile?.locationLat,
      currentUser?.profile?.locationLong,
      isAuthenticated,
      showMyEvents,
      showPendingEvents,
    ]
  )

  const sortFormMethods = useForm<{ sortBy?: string }>({
    defaultValues: { sortBy: resolvedSearch.sortBy },
  })

  useEffect(() => {
    sortFormMethods.reset({ sortBy: resolvedSearch.sortBy })
  }, [resolvedSearch.sortBy, sortFormMethods])

  const tagsJoin = (arr?: string[]) => (arr && arr.length > 0 ? arr.join(',') : undefined)

  const applyListFilters = useCallback(
    (values: EventListFilterFormValues, options: { includeSort: boolean }) => {
      setFilterNavigatePending(true)
      navigate({
        search: {
          ...searchRef.current,
          page: 1,
          name: values.name || undefined,
          distance: values.distance[0],
          categories: tagsJoin(values.categories),
          tags: tagsJoin(values.tags),
          excludeTags: tagsJoin(values.excludeTags),
          location: values.location?.name || undefined,
          locationLat: values.location?.latitude || undefined,
          locationLong: values.location?.longitude || undefined,
          pastEvents: values.pastEvents || undefined,
          showHosting: values.showHosting || undefined,
          ...(options.includeSort ? { sortBy: values.sortBy || undefined } : {}),
        },
        replace: true,
      })
    },
    [navigate]
  )

  const handleSortChange = useCallback(() => {
    const sortBy = sortFormMethods.getValues('sortBy')
    navigate({
      search: {
        ...searchRef.current,
        page: 1,
        sortBy: sortBy || undefined,
      },
      replace: true,
    })
  }, [navigate, sortFormMethods])

  const handlePageChange = useCallback(
    (nextPage: number): void => {
      if (!isAuthenticated && nextPage > 2) {
        const nextUrl = new URL(window.location.href)
        nextUrl.searchParams.set('page', String(nextPage))
        navigate({ to: '/auth/signup' } as never)
        return
      }
      navigate({ search: { ...searchRef.current, page: nextPage }, replace: true })
    },
    [isAuthenticated, navigate]
  )

  const handleReset = useCallback(() => {
    navigate({
      to: '/event/list',
      search: {
        ...(showMyEvents ? { myEvents: true } : {}),
        ...(showPendingEvents ? { pendingEvents: true } : {}),
      },
      replace: true,
    } as never)
  }, [navigate, showMyEvents, showPendingEvents])

  const getPagePreTitle = useCallback(() => {
    if (showMyEvents) return t('My')
    if (showPendingEvents) return t('Event')
    return t('Discover')
  }, [showMyEvents, showPendingEvents, t])

  const getBookmarkTitle = useCallback(() => {
    if (showMyEvents) return t('My Events')
    if (showPendingEvents) return t('Invitations')
    return t('Discover Events')
  }, [showMyEvents, showPendingEvents, t])

  const getPageTitle = useCallback(() => {
    if (showMyEvents) return t('Events')
    if (showPendingEvents) return t('Invitations')
    return t('Events')
  }, [showMyEvents, showPendingEvents, t])

  const getPageSubtitle = useCallback(() => {
    if (showMyEvents) return t('Browse events you are attending or have created.')
    if (showPendingEvents) return t('Browse events you were invited to.')
    return t('Browse a list of swinger and lifestyle events available on our platform.')
  }, [showMyEvents, showPendingEvents, t])

  const getActiveTab = useCallback(() => {
    if (showMyEvents) return 'myEvents'
    if (showPendingEvents) return 'pending'
    return 'discover'
  }, [showMyEvents, showPendingEvents])

  if (error) {
    return <ErrorAlert error={error} />
  }

  return (
    <main className="flex min-h-0 w-full flex-col md:flex-row">
      <aside className="hidden w-fit shrink-0 overflow-visible md:sticky md:top-14 md:flex md:h-[calc(100dvh-3.5rem)] md:flex-col md:self-start">
        <EventListSideNav
          defaultLocation={search.location ?? currentUser?.profile?.location}
          filterDefaults={listFilterDefaults}
          filterResetValues={listFilterResetValues}
          hasPermission={hasPermission}
          isAuthenticated={isAuthenticated}
          onFiltersApply={(values) => applyListFilters(values, { includeSort: false })}
          onReset={handleReset}
          showMyEvents={showMyEvents}
          tags={resolvedEventTags}
        />
      </aside>

      <div className="flex min-h-0 w-full min-w-0 flex-1 flex-col">
        <RouteLoadingIndicator loading={isFilterLoading} size="xs" variant="progress" />
        <div className="mx-auto w-full max-w-none px-4 py-2 md:px-8 lg:px-16">
          <PageHeader
            breadcrumbItems={[
              { label: t('Home'), to: '/' },
              { label: t('Event Categories'), to: '/event' },
              { label: getBookmarkTitle() },
            ]}
            className="items-stretch pb-0 pb-4 max-md:pb-0 md:mb-4"
            highlightTitle={getPageTitle()}
            preTitle={getPagePreTitle()}
            rightChild={
              <>
                <div className="flex items-start justify-end md:hidden">
                  <CreateEventButton />
                </div>
                <div className="hidden h-full flex-col items-end gap-3 md:flex">
                  <CreateEventButton />

                  <div className="mt-auto w-full">
                    <div className="flex flex-row items-center gap-2">
                      <span className="shrink-0 justify-self-center whitespace-nowrap font-medium text-muted-foreground text-sm">
                        {t('Sort By:')}
                      </span>
                      <Form formMethods={sortFormMethods} suppressHydrationWarning>
                        <NativeSelectFormField
                          fieldProps={{ className: 'min-w-0 flex-1' }}
                          name="sortBy"
                          onValueChange={handleSortChange}
                          selectProps={{ className: 'w-full min-w-[10rem]' }}
                        >
                          <option value="START_DATE">{t('Start Date')}</option>
                          <option value="DISTANCE">{t('Distance')}</option>
                          <option value="LATEST">{t('Latest')}</option>
                        </NativeSelectFormField>
                      </Form>
                      <LayoutToggleButton
                        layout={cardLayout}
                        onToggle={() => setCardLayout((prev) => (prev === 'compact' ? 'regular' : 'compact'))}
                      />
                    </div>
                  </div>
                </div>
              </>
            }
            subtitle={getPageSubtitle()}
          />

          <div className="mb-6 w-full md:hidden">
            <div className="mt-4">
              <EventListTopNav
                defaultLocation={search.location ?? currentUser?.profile?.location}
                filterDefaults={listFilterDefaults}
                filterResetValues={listFilterResetValues}
                hasPermission={hasPermission}
                isAuthenticated={isAuthenticated}
                onFiltersApply={(values) => applyListFilters(values, { includeSort: true })}
                onReset={handleReset}
                rightBarChildren={
                  <>
                    <CreateEventButton />
                    <LayoutToggleButton
                      layout={cardLayout}
                      onToggle={() => setCardLayout((prev) => (prev === 'compact' ? 'regular' : 'compact'))}
                    />
                  </>
                }
                showMyEvents={showMyEvents}
                tags={resolvedEventTags}
              />
            </div>
          </div>
          <div className="mb-8">
            <EventsMenu activeTab={getActiveTab()} listRoute="/event/list" />
          </div>

          {resolvedEventsPage.events.length > 0 ? (
            <div className="flex w-full flex-col items-stretch gap-0">
              <div
                className={cn(
                  'grid w-full items-stretch gap-4 md:gap-6',
                  cardLayout === 'compact' ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'
                )}
              >
                {resolvedEventsPage.events.map((event) => (
                  <EventCard cardLayout={cardLayout} event={event} key={event.id} />
                ))}
              </div>

              <PaginationControl
                count={resolvedEventsPage.count ? resolvedEventsPage.count : 1}
                onPageChange={(e) => handlePageChange(e.page)}
                page={resolvedSearch.page}
                pageSize={resolvedEventsPage.perPage ? resolvedEventsPage.perPage : 1}
              />
            </div>
          ) : (
            <Empty className="w-full pb-20">
              <EmptyHeader>
                <EmptyMedia variant="icon">
                  <CalendarIcon />
                </EmptyMedia>
                <EmptyTitle>{t('No events found')}</EmptyTitle>
                <EmptyDescription>{t('Try adjusting your search or filters.')}</EmptyDescription>
              </EmptyHeader>
            </Empty>
          )}
        </div>

        <Footer />
      </div>
    </main>
  )
}
